/**
 * The explicit interstitial for a redirect that dead-ends behind the
 * trfer-only gate (final whole-branch review, CRITICAL 1+2, 2026-09-21).
 *
 * Three admin addresses — `/admin/polls`, `/admin/polls/new` and
 * `/admin/polls/:id` — point at pages that live entirely inside
 * `<RoleGatedRoute allowedRoles={['trfer']} />`
 * (`App.tsx:596`). An admin whose ACTIVE role (`user.role`) is still `admin`
 * followed the old bare `<Navigate>` straight into that gate, failed it, and
 * was silently bounced to `/admin` with no explanation — a redirect to a
 * redirect that resolves to nothing.
 *
 * `/admin/handoff-config` was a sixth address here through 2026-09-20. It is
 * a real page again as of 2026-09-21 (DECISIONS 2026-09-20 amendment, Victor's
 * ruling that handoff config is real admin data) and no longer uses this
 * component at all — App.tsx mounts `AdminHandoffConfigPage` there directly.
 *
 * The fix is deliberately NOT an automatic `switchRole('trfer')` call. Role
 * switching persists server-side (`POST /api/auth/switch-role`,
 * `AuthContext.tsx:462`) — it is a real account-state change, not a client
 * render decision, so making it happen as a SIDE EFFECT of following a stale
 * link would flip the reader's active role without their asking for it and
 * without anything on screen saying so. This component asks first.
 *
 * Three states:
 *   1. Already wearing the `trfer` hat — nothing to ask, redirect straight
 *      through. (Not reachable through the routes above today, since they
 *      sit behind an admin-only gate, but the component does not assume
 *      that of every future caller.)
 *   2. Holds `trfer` among `user.roles` but is not currently wearing it —
 *      show the panel and let a click do the switch, then land on `to`.
 *   3. Does not hold `trfer` at all — say so plainly. Offering a button that
 *      the server will refuse is worse than no button.
 */
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRightLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { PageHead, Emphasis } from '@/components/editorial';

/** The desk a redirect is pointing at. */
export type SwitchRoleTarget = 'trfer' | 'admin';

const DESK_WORDS: Record<SwitchRoleTarget, { desk: string; role: string }> = {
  trfer: { desk: 'TRFer desk', role: 'TRFer' },
  admin: { desk: 'admin console', role: 'admin' },
};

export interface SwitchRoleToContinueProps {
  /** Where to land, once the active role is `role`. */
  to: string;
  /**
   * The hat needed at `to`. Defaults to `trfer`, the direction this component
   * was built for — stale `/admin/*` links into pages that moved to the TRFer
   * desk on 2026-09-20.
   *
   * Since 2026-09-22 the ledger points the OTHER way: it moved to /admin
   * (Victor: "generated 0 engagement … move it to admin"), so the
   * `/trfer/ledger/*` addresses survive as redirects INTO the admin console.
   * A TRFer who does not hold admin is told so, rather than offered a button
   * the server would refuse — the same rule this component already applied in
   * the other direction.
   */
  role?: SwitchRoleTarget;
}

export function SwitchRoleToContinue({ to, role: target = 'trfer' }: SwitchRoleToContinueProps) {
  const { user, switchRole } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const [failed, setFailed] = useState(false);

  // Case 1 — nothing to ask.
  if (user?.role === target) {
    return <Navigate to={to} replace />;
  }

  const holdsTarget = (user?.roles ?? []).includes(target);
  const words = DESK_WORDS[target];

  const onSwitch = async () => {
    setFailed(false);
    setSwitching(true);
    try {
      await switchRole(target);
      // `switchRole` itself navigates to that role's home first
      // (`AuthContext.tsx:480`); this replaces that with the address the
      // reader actually asked for.
      navigate(to, { replace: true });
    } catch {
      setFailed(true);
      setSwitching(false);
    }
  };

  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center overflow-y-auto bg-stevens-bg px-6 py-16">
      <div className="w-full max-w-md text-center">
        <PageHead
          density="compact"
          eyebrow="Switch role to continue"
          icon={ArrowRightLeft}
          title={<>This lives in the <Emphasis>{words.desk}</Emphasis> now.</>}
          lede={
            holdsTarget
              ? `Switch to the ${words.role} role to open it — you can switch back from the sidebar any time.`
              : `Your account does not hold the ${words.role} role, so there is nothing to switch to.`
          }
        />
        {holdsTarget ? (
          <Button
            className="mt-6"
            variant="brand"
            onClick={onSwitch}
            disabled={switching}
          >
            {switching ? 'Switching…' : `Switch to ${words.role} and continue`}
          </Button>
        ) : null}
        {failed && (
          <p className="mt-3 text-caption text-stevens-ink-3">
            Could not switch roles. Try again.
          </p>
        )}
      </div>
    </div>
  );
}
